import { action, computed, makeObservable, observable } from 'mobx';
import toast from 'react-hot-toast';
import URLConstants from '../../constants/url-constants';
import config from '../../helpers/config-helper';
import { IMultiSelectOption, IReactSelectOption } from '../../models/ICommon';
import IApiResponse, {
  IApiSuccessResponse,
} from '../../models/response/IApiResponse';
import { formatMessage } from '../../translations/format-message';
import {
  GetBOMSearchResultsState,
  ObservableInitialState,
} from '../initialState';
import * as baseService from '../service/base-service';
import { IBOMSearchFilter, bOMGroups } from '../../models/IBOMSearchFilter';
import {
  IBOMSearchResult,
  IBOMSearchResultListVM,
} from '../../models/response/IBOMSearchResponse';
import {
  IMaterialTagAssignList,
  IMaterialTagAssignListVM,
} from '../../models/response/IMaterialTagAssignResponse';
import { IPartsSearchListVM } from '../../models/response/IPartsSearchResponse';
import { fixTimezoneOffsetYYYYMMDD } from '../../constants/constants';
const appConfig = config();

export class BOMStore {
  initialStateValue = ObservableInitialState;
  BOMSearchResultsList: IBOMSearchResultListVM = GetBOMSearchResultsState;
  getAllOrderTableViewState = { ...this.initialStateValue };
  getBOMSearchResultsServiceState = { ...this.initialStateValue };
  getMaterialTagAssignDetailsServiceState = { ...this.initialStateValue };
  MaterialTagAssignDetails: IMaterialTagAssignListVM = null;
  getPartsSearchResultsServiceState = { ...this.initialStateValue };
  PartsSearchList: IPartsSearchListVM = null;
  getPartsSearchColumnListState = { ...this.initialStateValue };
  partsSearchColumnList: string[] = [];
  getMaterialKatashikiByTrayIdState = { ...this.initialStateValue };
  materialKatashikiByTrayId: string[];

  constructor() {
    makeObservable(this, {
      BOMSearchResultsList: observable,
      getAllOrderTableViewState: observable,
      getBOMSearchResultsServiceState: observable,
      getMaterialTagAssignDetailsServiceState: observable,
      MaterialTagAssignDetails: observable,
      getPartsSearchResultsServiceState: observable,
      PartsSearchList: observable,
      getPartsSearchColumnListState: observable,
      partsSearchColumnList: observable,
      getMaterialKatashikiByTrayIdState: observable,
      materialKatashikiByTrayId: observable,

      GetBOMSearchResultsService: action,
      GetMaterialTagAssignDetailsService: action,
      GetPartsSearchResultsService: action,
      GetPartsSearchColumnsService: action,
      GetMaterialKatashikiByTrayIdService: action,

      reset: action,
      resetGetBOMSearchResultsServiceState: action,
      resetGetMaterialTagAssignDetailsServiceState: action,
      resetGetPartsSearchResultsServiceState: action,
      resetGetPartsSearchColumnListState: action,
      resetGetMaterialKatashikiByTrayIdState: action,

      allBOMSearchResults: computed,
      materialTagAssignDetails: computed,
      katashikiList: computed,
      partsKatashikiList: computed,
      partSearchGridData: computed,
      allDimensionHeaders: computed,
      visiblePartsSearchColumnsList: computed,
      materialKatashikiOptionsByTrayId: computed,
      resetStore: action,
    });
  }

  //#region Actions
  GetBOMSearchResultsService = (
    tenantId: number,
    currentPage: number,
    pageLimit: number,
    searchTerm: IBOMSearchFilter
  ) => {
    this.getBOMSearchResultsServiceState.inProgress = true;
    const url = URLConstants.BOMSearchResults;
    const data = {
      TenantId: tenantId,
      PagingDetail: {
        CurrentPage: currentPage,
        PageSize: pageLimit,
        TotalPages: 0,
        TotalRecords: 0,
      },
      filter: searchTerm,
    };
    return baseService
      .postRequest(url, data)
      .then(
        (
          response: IApiResponse<IApiSuccessResponse<IBOMSearchResultListVM>>
        ) => {
          if (response.data.Error) {
            this.getBOMSearchResultsServiceState.error = response.data.Message;
            toast.error(formatMessage(response.data.Message));
          } else {
            this.BOMSearchResultsList = response.data.Data;
          }
        }
      )
      .catch((err: any) => {
        this.getBOMSearchResultsServiceState.error = err;
      })
      .finally(
        action(() => {
          this.getBOMSearchResultsServiceState.inProgress = false;
        })
      );
  };

  GetPartsSearchColumnsService = (tenantId: number) => {
    this.getPartsSearchColumnListState.inProgress = true;
    const url = URLConstants.PartsSearchColumns + '?TenantId=' + tenantId;
    return baseService
      .getRequest(url)
      .then((response: IApiResponse<IApiSuccessResponse<string[]>>) => {
        if (response.data.Error) {
          this.getPartsSearchColumnListState.error = response.data.Message;
          toast.error(formatMessage(response.data.Message));
        } else {
          this.partsSearchColumnList = response.data.Data;
          this.getPartsSearchColumnListState.success = true;
        }
      })
      .catch((err: string) => {
        this.getPartsSearchColumnListState.error = err;
      })
      .finally(
        action(() => {
          this.getPartsSearchColumnListState.inProgress = false;
        })
      );
  };

  GetMaterialTagAssignDetailsService = (
    tenantId: number,
    currentPage: number,
    pageLimit: number,
    materialProductIds: string[],
    materialKatashiki: string[],
    uiFilter: boolean,
    filter: IBOMSearchFilter,
    bOMGroups: bOMGroups
  ) => {
    this.getMaterialTagAssignDetailsServiceState.inProgress = true;
    const url = URLConstants.MaterialTagAssign;
    const data = {
      TenantId: tenantId,
      MaterialProductId: materialProductIds,
      MaterialKatashiki: materialKatashiki,
      UiFilter: uiFilter,
      filter: filter,
      PagingDetails: {
        CurrentPage: currentPage,
        PageSize: pageLimit,
        TotalPages: 0,
        TotalRecords: 0,
      },
      bOMGroups: bOMGroups,
    };
    return baseService
      .postRequest(url, data)
      .then(
        (
          response: IApiResponse<IApiSuccessResponse<IMaterialTagAssignListVM>>
        ) => {
          if (response.data.Error) {
            this.getMaterialTagAssignDetailsServiceState.error =
              response.data.Message;
            toast.error(formatMessage(response.data.Message));
          } else {
            this.MaterialTagAssignDetails = response.data.Data;
          }
        }
      )
      .catch((err: any) => {
        this.getMaterialTagAssignDetailsServiceState.error = err;
      })
      .finally(
        action(() => {
          this.getMaterialTagAssignDetailsServiceState.inProgress = false;
        })
      );
  };

  GetPartsSearchResultsService = (
    tenantId: number,
    currentPage: number,
    pageLimit: number,
    materialProductIds: string[],
    materialKatashiki: string[],
    uiFilter: boolean,
    filter: IBOMSearchFilter,
    bOMGroups: bOMGroups
  ) => {
    this.getPartsSearchResultsServiceState.inProgress = true;
    const url = URLConstants.PartsSearch;
    const data = {
      TenantId: tenantId,
      MaterialProductId: materialProductIds,
      MaterialKatashiki: materialKatashiki,
      UiFilter: uiFilter,
      filter: filter,
      bOMGroups: bOMGroups,
      PagingDetail: {
        CurrentPage: currentPage,
        PageSize: pageLimit,
        TotalPages: 0,
        TotalRecords: 0,
      },
    };
    return baseService
      .postRequest(url, data)
      .then(
        (response: IApiResponse<IApiSuccessResponse<IPartsSearchListVM>>) => {
          if (response.data.Error) {
            this.getPartsSearchResultsServiceState.error =
              response.data.Message;
            toast.error(formatMessage(response.data.Message));
          } else {
            this.PartsSearchList = response.data.Data;
          }
        }
      )
      .catch((err: any) => {
        this.getPartsSearchResultsServiceState.error = err;
      })
      .finally(
        action(() => {
          this.getPartsSearchResultsServiceState.inProgress = false;
        })
      );
  };

  GetMaterialKatashikiByTrayIdService = (tenantId: number, trayId: string) => {
    this.getBOMSearchResultsServiceState.inProgress = true;
    const url = `${URLConstants.GetMaterialKatashikiByTrayId}/${tenantId}?TrayId=${trayId}`;
    return baseService
      .getRequest(url)
      .then((response: IApiResponse<IApiSuccessResponse<string[]>>) => {
        if (response.data.Error) {
          this.getBOMSearchResultsServiceState.error = response.data.Message;
          toast.error(formatMessage(response.data.Message));
        } else {
          this.materialKatashikiByTrayId = response.data.Data;
        }
      })
      .catch((err: any) => {
        this.getBOMSearchResultsServiceState.error = err;
      })
      .finally(
        action(() => {
          this.getBOMSearchResultsServiceState.inProgress = false;
        })
      );
  };
  //#endregion

  //#region Computed
  get allBOMSearchResults(): IBOMSearchResult[] {
    let processData: IBOMSearchResult[] = [];
    if (
      this.BOMSearchResultsList?.BOMSearchResults &&
      this.BOMSearchResultsList.BOMSearchResults?.length > 0
    ) {
      this.BOMSearchResultsList.BOMSearchResults.map((process, index) => {
        processData.push({
          serialnumber: index + 1,
          MaterialProductId: process.MaterialProductId || '',
          MaterialKatashiki: process.MaterialKatashiki || '',
          // MaterialKatashiki2: process.MaterialKatashiki2 || '',
          MaterialRequiredUnits: process.MaterialRequiredUnits || 0,
          MaterialManufacturerName: process.MaterialManufacturerName || '',
          AppearedInTrayId: process.AppearedInTrayId || 0,
          SubContractorName: process.SubContractorName || '',
        });
      });
    }
    return processData;
  }

  get visiblePartsSearchColumnsList() {
    if (
      this.PartsSearchList?.SavedColumn &&
      this.PartsSearchList?.SavedColumn.length > 0
    ) {
      return this.PartsSearchList?.SavedColumn.split(',').map((opt) => ({
        label: opt,
        value: opt,
      }));
    }
    return [];
  }

  get materialTagAssignDetails(): IMaterialTagAssignList[] {
    let data: IMaterialTagAssignList[] = [];
    if (
      this.MaterialTagAssignDetails?.MaterialAssign &&
      this.MaterialTagAssignDetails?.MaterialAssign?.length > 0
    ) {
      this.MaterialTagAssignDetails.MaterialAssign.map((assign) => {
        data.push({
          TagId: assign.TagId || '',
          EndUserOrderId: assign.EndUserOrderId || '',
          OutOfStock: assign.OutOfStock || '',
          ProductId: assign.ProductId || '',
          TrayId: assign.TrayId || '',
          ProductComment1: assign.ProductComment1 || '',
          ProductComment2: assign.ProductComment2 || '',
        });
      });
    }
    return data;
  }

  get katashikiList() {
    const list: IMultiSelectOption[] = [];
    if (
      this.MaterialTagAssignDetails?.Katashiki &&
      this.MaterialTagAssignDetails?.Katashiki?.length > 0
    ) {
      this.MaterialTagAssignDetails.Katashiki.map((katashiki) => {
        list.push({
          label: katashiki,
          value: katashiki,
        });
      });
    }
    return list;
  }

  get partSearchGridData(): any[] {
    let data: any[] = [];
    if (
      this.PartsSearchList &&
      this.PartsSearchList.PartSearchList.length > 0
    ) {
      this.PartsSearchList.PartSearchList.forEach((item, index) => {
        let dimensionMap: any = {};
        item.Dimensions?.forEach((dim) => {
          const value = Array.isArray(dim.Value)
            ? dim.Value.join(', ')
            : dim.Value;

          if (!dimensionMap[dim.Name] || dimensionMap[dim.Name] === '-') {
            dimensionMap[dim.Name] = value;
          }
        });
        data.push({
          RowKey: `${item.TrayId || ''}-${item.MaterialProductId || ''}-${index}`,
          MaterialProductId: item.MaterialProductId || '',
          DeliveryDate: fixTimezoneOffsetYYYYMMDD(item.DeliveryDate) || '',
          TrayId: item.TrayId || '',
          Kishu: item.kishu || '',
          ProductId: item.ProductId || '',
          ...dimensionMap,
        });
      });
    }
    return data;
  }

  get columnListDetails() {
    const list: IMultiSelectOption[] = [];
    if (this.partsSearchColumnList && this.partsSearchColumnList.length > 0) {
      this.partsSearchColumnList
        .filter((col) => col !== 'Planed Due Date') // Hide the Planed Due Date Column
        .map((column) =>
          list.push({
            label: column,
            // label: column.replace(/\s+/g, '_'),
            // value: column.replace(/ /g, ''),
            value: column,
          })
        );
    }
    return list;
  }

  get dimensionColumns(): string[] {
    let cols: string[] = [];

    if (
      this.PartsSearchList?.PartSearchList &&
      this.PartsSearchList.PartSearchList.length > 0
    ) {
      this.PartsSearchList.PartSearchList.forEach((item) => {
        item.Dimensions?.forEach((dim) => {
          if (!cols.includes(dim.Name)) {
            cols.push(dim.Name);
          }
        });
      });
    }

    return cols;
  }

  get allDimensionHeaders(): string[] {
    const dimensionHeaders = new Set<string>();

    if (this.PartsSearchList?.PartSearchList?.length > 0) {
      this.PartsSearchList.PartSearchList.forEach(({ Dimensions }) => {
        Dimensions?.forEach((dimension) => {
          if (dimension?.Name) {
            dimensionHeaders.add(dimension.Name);
          }
        });
      });
    }

    return Array.from(dimensionHeaders);
  }

  get partsKatashikiList() {
    const list: IMultiSelectOption[] = [];
    if (
      this.PartsSearchList?.Katashiki &&
      this.PartsSearchList?.Katashiki?.length > 0
    ) {
      this.PartsSearchList.Katashiki.map((katashiki) => {
        list.push({
          label: katashiki || '--',
          value: katashiki || '',
        });
      });
    }
    return list;
  }

  get materialKatashikiOptionsByTrayId(): IReactSelectOption[] {
    const options: IReactSelectOption[] = [];
    if (
      this.materialKatashikiByTrayId &&
      this.materialKatashikiByTrayId.length > 0
    ) {
      this.materialKatashikiByTrayId.map((option) => {
        options.push({
          label: option,
          value: option,
        });
      });
    }
    return options;
  }
  //#endregion

  //#region Reset
  reset = () => {
    this.BOMSearchResultsList = GetBOMSearchResultsState;
  };

  resetGetBOMSearchResultsServiceState = () => {
    this.getBOMSearchResultsServiceState = { ...this.initialStateValue };
  };

  resetGetMaterialTagAssignDetailsServiceState = () => {
    this.getMaterialTagAssignDetailsServiceState = {
      ...this.initialStateValue,
    };
  };

  resetGetPartsSearchResultsServiceState = () => {
    this.getPartsSearchResultsServiceState = {
      ...this.initialStateValue,
    };
  };

  resetGetPartsSearchColumnListState = () => {
    this.getPartsSearchColumnListState = { ...this.initialStateValue };
  };

  resetGetMaterialKatashikiByTrayIdState = () => {
    this.getMaterialKatashikiByTrayIdState = { ...this.initialStateValue };
  };

  /**
   * This function is used to reset all store observables to their initial values.
   * @returns
   */
  resetStore = () => {
    this.BOMSearchResultsList = GetBOMSearchResultsState;
    this.getAllOrderTableViewState = { ...this.initialStateValue };
    this.getBOMSearchResultsServiceState = { ...this.initialStateValue };
    this.getMaterialTagAssignDetailsServiceState = { ...this.initialStateValue };
    this.MaterialTagAssignDetails = null;
    this.getPartsSearchResultsServiceState = { ...this.initialStateValue };
    this.PartsSearchList = null;
    this.getPartsSearchColumnListState = { ...this.initialStateValue };
    this.partsSearchColumnList = [];
    this.getMaterialKatashikiByTrayIdState = { ...this.initialStateValue };
    this.materialKatashikiByTrayId = undefined;
  };
  //#endregion
}

export default new BOMStore();
